import { Alert, TextField, Typography } from '@mui/material'
import { useEffect, useState } from 'react'

import { UserFragment } from '@/graphql'
import { usePinAvailability } from '@/hooks/usePinAvailability'
import useT from '@/i18n/useT'

type PinInputProps = {
  value: string
  onChange: (newValue: string) => void
  onValidityChange?: (isValid: boolean) => void
  user?: UserFragment
  required?: boolean
}

function PinInputField({
  value,
  onChange,
  onValidityChange,
  user,
  required = false,
}: PinInputProps) {
  const { t } = useT()
  const [hasInteracted, setHasInteracted] = useState(false)
  const [pinMessage, setPinMessage] = useState<string | null>(null)
  const { isAvailable, loading: pinCheckLoading } = usePinAvailability(
    value,
    user?.id,
  )

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    if (/^\d{0,10}$/.test(newValue)) {
      if (!hasInteracted) setHasInteracted(true)
      onChange(newValue)
    }
  }

  const isTooShortOrLong =
    value.length > 0 && (value.length < 4 || value.length > 10)
  const isPinValid: boolean =
    value.length >= 4 && value.length <= 10 && !!isAvailable

  useEffect(() => {
    if (value && !hasInteracted) {
      onChange(value)
    }
  }, [value])

  useEffect(() => {
    if (onValidityChange) {
      onValidityChange(isPinValid)
    }
  }, [isPinValid, onValidityChange])

  useEffect(() => {
    if (!value || value.length < 4 || value.length > 10 || !hasInteracted) {
      setPinMessage(null)
      return
    }

    if (!pinCheckLoading) {
      setPinMessage(
        isAvailable ? t('pinInput.pinIsAvailable') : t('pinInput.pinIsTaken'),
      )
    }
  }, [value, isAvailable, hasInteracted, pinCheckLoading, t])

  return (
    <>
      <TextField
        label={t('pinInput.label')}
        value={value}
        onChange={handleChange}
        error={
          isTooShortOrLong ||
          (hasInteracted && value.length >= 4 && !isAvailable)
        }
        helperText={isTooShortOrLong ? t('pinInput.helperText') : ''}
        variant="outlined"
        required={required}
        fullWidth
        sx={{ mt: 2 }}
      />
      {value && pinCheckLoading && value.length >= 4 && hasInteracted && (
        <Typography variant="body2" color="textSecondary">
          {t('pinInput.checkingAvailability')}
        </Typography>
      )}
      {pinMessage && !pinCheckLoading && (
        <Alert
          severity={
            pinMessage.includes(t('pinInput.pinIsAvailable'))
              ? 'success'
              : 'error'
          }
          sx={{ mt: 2, mb: 2 }}
        >
          {pinMessage}
        </Alert>
      )}
    </>
  )
}

export default PinInputField
